import CategoryIcon from '@mui/icons-material/Category';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import GenericDrawer from '@/components/GenericDrawer';
import { Product } from '@/types/fulfill/inventory';

import KitThumbnailStack from './KitThumbnailStack';

interface KitCompositionDrawerProps {
  product: Product | null;
  open: boolean;
  onClose: () => void;
}

const KitCompositionDrawer = ({ product, open, onClose }: KitCompositionDrawerProps) => {
  const theme = useTheme();
  const components = product?.kit_components ?? [];
  const totalPieces = components.reduce((sum, component) => sum + component.quantity, 0);

  return (
    <GenericDrawer
      open={open}
      onClose={onClose}
      title="Composición del Kit"
      iconTitle={<CategoryIcon color="primary" />}
    >
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, mb: 3 }}>
          <KitThumbnailStack components={components} />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {product?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
              SKU {product?.sku}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ color: theme.palette.grey[600], mb: 1 }}
        >
          Este Kit está compuesto por:
        </Typography>

        <Box
          sx={{
            border: '1px solid',
            borderColor: theme.palette.grey[300],
            borderRadius: '1rem',
            overflow: 'hidden',
          }}
        >
          {components.length === 0 && (
            <Box sx={{ p: 2, backgroundColor: theme.palette.common.white }}>
              <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
                Este Kit no tiene componentes registrados.
              </Typography>
            </Box>
          )}
          {components.map((component, index) => (
            <Box key={component.sku}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  backgroundColor: theme.palette.common.white,
                }}
              >
                <img
                  src={component.thumbnail}
                  alt={component.name}
                  style={{ width: 48, height: 48, borderRadius: '0.75rem', objectFit: 'cover' }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {component.name ?? 'Producto sin nombre'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
                    SKU {component.sku}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight="bold" color={theme.palette.primary.main}>
                  {component.quantity} {component.quantity === 1 ? 'pieza' : 'piezas'}
                </Typography>
              </Box>
              {index < components.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>

        <Typography variant="body2" fontWeight="bold" sx={{ mt: 2, textAlign: 'right' }}>
          Total de piezas: {totalPieces}
        </Typography>
      </Box>
    </GenericDrawer>
  );
};

export default KitCompositionDrawer;
